import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  User,
  FileText,
  AlertCircle,
  RefreshCw,
  BookOpen,
  Eye,
  Star,
  Scale,
  Calculator,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  X,
} from "lucide-react";
import ProcessNumber from "../components/common/ProcessNumber";
import PublicationDjeDetailsSidebar from "../components/publications-dje/PublicationDjeDetailsSidebar";
import ProcessAnalysisPanel from "../components/processes/ProcessAnalysisPanel";
import ProcessCNJDataPanel from "../components/processes/ProcessCNJDataPanel";
import ProcessMovementsPanel from "../components/processes/ProcessMovementsPanel";
import ProcessDocumentsPanel from "../components/processes/ProcessDocumentsPanel";
import MemorandoContent from "../components/processes/MemorandoContent";
import apiService from "../services/api";
import { processesService } from "../services/processesService";
import { useAuth } from "../context/AuthContext";
import { publicationsDjeService } from "../services";
import { safeParseAnalysis, extractProcessContactHints } from "../utils/processAnalysis";
import { resolveProcessosReturnTo } from "../utils/processosListNavigation";
import PartnerLawyerBadge from "../components/common/PartnerLawyerBadge";
import { lawyerPartnersService } from "../services/lawyerPartnersService";
import {
  findMatchingLawyerPartners,
  extractActiveLawyerCandidates,
  filterDisplayPoles,
} from "../utils/lawyerPartners";
import { decodeHtmlEntities } from "../utils/htmlEntities";
import {
  getProcessNumberDigits,
  mapDjePublicationToListItem,
  sortProcessPublications,
} from "../utils/processPublications";

const PROCESS_PUBLICATIONS_DISPLAY_LIMIT = 5;
const PROCESS_PUBLICATIONS_FETCH_LIMIT = 20;

function CopyButton({ text, label = "Copiar", className = "" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const value = typeof text === "string" ? text : String(text ?? "");
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
        copied
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800"
      } ${className}`}
      title={copied ? "Copiado!" : label}
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5" aria-hidden />
          Copiado
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" aria-hidden />
          {label}
        </>
      )}
    </button>
  );
}

function CalculosValorPresenteSection({ calculos, variant = "embedded" }) {
  if (!calculos) return null;

  const heading =
    variant === "embedded" ? (
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center">
          <Calculator className="w-4 h-4 text-gray-600 mr-2" />
          Cálculo de Valor Presente
        </h4>
        {calculos?.creditType && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
            {String(calculos.creditType).replace(/_/g, " ")}
          </span>
        )}
      </div>
    ) : (
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900 flex items-center">
          <Calculator className="w-4 h-4 text-gray-600 mr-2" />
          Cálculos (Valor Presente)
        </h3>
        {calculos?.creditType && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
            {String(calculos.creditType).replace(/_/g, " ")}
          </span>
        )}
      </div>
    );

  const body = (
    <>
      {calculos.baseValue ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Valor base (estimado)
            </p>
            <p className="text-sm text-gray-900 font-semibold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(Number(calculos.baseValue))}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Prazo estimado: {Number(calculos.expectedHorizonMonths || 0)} meses
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Valor presente (estimado)
            </p>
            <p className="text-sm text-gray-900 font-semibold">
              {calculos.presentValueEstimate
                ? new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(Number(calculos.presentValueEstimate))
                : "N/A"}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Desconto: {Math.round(Number(calculos.discountRateAnnual || 0) * 100)}% a.a. / Correção:{" "}
              {Math.round(Number(calculos.correctionRateAnnual || 0) * 100)}% a.a.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          Não há valor base monetário suficiente para calcular o valor presente. Informe o valor estimado no cadastro do
          processo ou garanta a extração de valores na análise Kortex para habilitar o cálculo.
        </div>
      )}

      {calculos?.methodology && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Metodologia</p>
          <p className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">{String(calculos.methodology)}</p>
        </div>
      )}
    </>
  );

  if (variant === "standalone") {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        {heading}
        {body}
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-100">
      {heading}
      {body}
    </div>
  );
}

function formatCurrencyBRL(value) {
  if (value == null || Number.isNaN(Number(value))) return "N/A";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));
}

function formatIsoDateBR(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return "N/A";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function CalculosCreditoSection({ calculoPrecatorio, variant = "embedded" }) {
  if (!calculoPrecatorio) return null;

  const isTrabalhista = calculoPrecatorio.calculationType === "TRABALHISTA";
  const sectionTitle = isTrabalhista
    ? "Cálculo de Crédito Trabalhista"
    : "Cálculo Jurídico do Precatório";
  const laborDetails = calculoPrecatorio.laborDetails;
  const updatedSnapshot = laborDetails?.updatedSnapshot;
  const purchaseSnapshot = laborDetails?.purchaseSnapshot;

  const heading =
    variant === "embedded" ? (
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center">
          <Calculator className="w-4 h-4 text-gray-600 mr-2" />
          {sectionTitle}
        </h4>
        {calculoPrecatorio?.status && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
            {String(calculoPrecatorio.status).replace(/_/g, " ")}
          </span>
        )}
      </div>
    ) : (
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900 flex items-center">
          <Calculator className="w-4 h-4 text-gray-600 mr-2" />
          {sectionTitle}
        </h3>
        {calculoPrecatorio?.status && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
            {String(calculoPrecatorio.status).replace(/_/g, " ")}
          </span>
        )}
      </div>
    );

  const body = (
    <>
      {calculoPrecatorio.updatedAmount != null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {isTrabalhista ? "Total crédito bruto (compra)" : "Valor principal"}
            </p>
            <p className="text-sm text-gray-900 font-semibold">
              {formatCurrencyBRL(calculoPrecatorio.principalAmount)}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Data-base: {formatIsoDateBR(calculoPrecatorio.baseDate)}
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {isTrabalhista ? "Total crédito bruto (atualizado)" : "Valor atualizado"}
            </p>
            <p className="text-sm text-gray-900 font-semibold">
              {formatCurrencyBRL(calculoPrecatorio.updatedAmount)}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Atualizado até: {formatIsoDateBR(calculoPrecatorio.endDate)}
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          {isTrabalhista
            ? "Cálculo trabalhista indisponível. Informe valor estimado ou dados estruturados de crédito trabalhista."
            : "Cálculo jurídico indisponível para este processo. Verifique valor estimado, data-base e índices necessários."}
        </div>
      )}

      {isTrabalhista && updatedSnapshot && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Face reclamante
            </p>
            <p className="text-sm text-gray-900 font-semibold">
              {formatCurrencyBRL(updatedSnapshot.faceReclamante)}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Compra: {formatCurrencyBRL(updatedSnapshot.precoCompraReclamante)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Face honorários
            </p>
            <p className="text-sm text-gray-900 font-semibold">
              {formatCurrencyBRL(updatedSnapshot.faceHonorarios)}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Compra: {formatCurrencyBRL(updatedSnapshot.precoCompraHonorarios)}
            </p>
          </div>
        </div>
      )}

      {isTrabalhista && purchaseSnapshot && updatedSnapshot && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              FGTS
            </p>
            <p className="text-gray-900">{formatCurrencyBRL(updatedSnapshot.totalFgts)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Valor líquido
            </p>
            <p className="text-gray-900">{formatCurrencyBRL(updatedSnapshot.valorLiquido)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Juros mensais
            </p>
            <p className="text-gray-900">
              {((laborDetails?.monthlyInterestRate ?? 0) * 100).toFixed(2)}% a.m.
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        {calculoPrecatorio.primaryRegime && (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              {isTrabalhista ? "Regime de atualização" : "Regime principal"}
            </p>
            <p className="text-gray-900">{String(calculoPrecatorio.primaryRegime).replace(/_/g, " ")}</p>
          </div>
        )}
        {!isTrabalhista && calculoPrecatorio.debtorEntity && (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Ente devedor
            </p>
            <p className="text-gray-900 capitalize">{calculoPrecatorio.debtorEntity}</p>
          </div>
        )}
        {!isTrabalhista && calculoPrecatorio.requisitionType && (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Tipo
            </p>
            <p className="text-gray-900">{String(calculoPrecatorio.requisitionType).replace(/_/g, " ")}</p>
          </div>
        )}
      </div>

      {Array.isArray(calculoPrecatorio.legalReferences) && calculoPrecatorio.legalReferences.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Bases legais
          </p>
          <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
            {calculoPrecatorio.legalReferences.map((ref, index) => (
              <li key={`legal-ref-${index}`}>{ref}</li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(calculoPrecatorio.warnings) && calculoPrecatorio.warnings.length > 0 && (
        <div className="mt-4 space-y-2">
          {calculoPrecatorio.warnings.map((warning, index) => (
            <div
              key={`prec-warn-${index}`}
              className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800"
            >
              {String(warning)}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(calculoPrecatorio.alerts) && calculoPrecatorio.alerts.length > 0 && (
        <div className="mt-4 space-y-2">
          {calculoPrecatorio.alerts.map((alert, index) => (
            <div
              key={`prec-alert-${index}`}
              className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800"
            >
              {String(alert)}
            </div>
          ))}
        </div>
      )}

      {calculoPrecatorio?.methodology && (
        <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
            Metodologia
          </p>
          <p className="text-sm text-indigo-900 leading-relaxed whitespace-pre-wrap">
            {String(calculoPrecatorio.methodology)}
          </p>
        </div>
      )}

      {calculoPrecatorio?.indexMetadata && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Índices oficiais
          </p>
          <p className="text-sm text-gray-800">
            Versão: {String(calculoPrecatorio.indexMetadata.version)}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            Período: {calculoPrecatorio.indexMetadata.periodStart} a{" "}
            {calculoPrecatorio.indexMetadata.periodEnd}
          </p>
          {calculoPrecatorio.indexMetadata.sources && (
            <ul className="mt-2 list-disc list-inside text-xs text-gray-600 space-y-1">
              {Object.entries(calculoPrecatorio.indexMetadata.sources).map(([key, label]) => (
                <li key={key}>
                  {key}: {String(label)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );

  if (variant === "standalone") {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        {heading}
        {body}
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-100">
      {heading}
      {body}
    </div>
  );
}

/** Filtro da API de movimentações (movements-api.md). A consulta Kortex usa `Ambos`. */
const SECOND_DEGREE_INSTANCE_NAME = "2º grau";

function movementIdentity(movement) {
  if (movement?.id) return String(movement.id);
  return [
    movement?.index_movement,
    movement?.date_event,
    movement?.instance_name,
    movement?.description,
  ].join("|");
}

function mergeMovementPages(primary, extraItems = []) {
  const base = primary?.data || [];
  const seen = new Set(base.map(movementIdentity));
  const uniqueExtra = extraItems.filter(
    (movement) => !seen.has(movementIdentity(movement)),
  );
  if (!uniqueExtra.length) return primary;
  return {
    ...primary,
    data: [...base, ...uniqueExtra],
  };
}

export default function ProcessoDetalhesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const goBackToProcessList = () => {
    navigate(resolveProcessosReturnTo(id, location.state?.returnTo), {
      replace: true,
    });
  };
  const { user } = useAuth();
  const isAdmin =
    user?.role === "ADMIN" ||
    String(user?.role || "").toLowerCase() === "admin";
  const [processo, setProcesso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicacoes, setPublicacoes] = useState([]);
  const [publicacoesLoading, setPublicacoesLoading] = useState(false);
  const [publicacoesError, setPublicacoesError] = useState(null);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [isPublicationSidebarOpen, setIsPublicationSidebarOpen] = useState(false);
  const [loadingPublication, setLoadingPublication] = useState(false);
  const [cnjData, setCnjData] = useState(null);
  const [cnjLoading, setCnjLoading] = useState(false);
  const [lawyerPartners, setLawyerPartners] = useState([]);
  const [convertingToLead, setConvertingToLead] = useState(false);
  const [rejectingProcess, setRejectingProcess] = useState(false);
  const [restoringProcess, setRestoringProcess] = useState(false);
  const [memorandoGenerating, setMemorandoGenerating] = useState(false);
  const [memorandoError, setMemorandoError] = useState(null);
  const [refreshingKortex, setRefreshingKortex] = useState(false);

  
  const [cnjNotFound, setCnjNotFound] = useState(false);// Estados para controlar expansão dos cards
  const [isCNJDataExpanded, setIsCNJDataExpanded] = useState(false);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(false);
  const [isPublicationsExpanded, setIsPublicationsExpanded] = useState(false);
  const [isParecerExpanded, setIsParecerExpanded] = useState(false);
  const [isMovementsExpanded, setIsMovementsExpanded] = useState(false);
  const [movementsResponse, setMovementsResponse] = useState(null);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsLoadingMore, setMovementsLoadingMore] = useState(false);
  const [movementsError, setMovementsError] = useState(null);
  const [movementsPage, setMovementsPage] = useState(1);
  const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(false);
  const [documentsResponse, setDocumentsResponse] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);

  const loadLawyerPartners = async () => {
    try {
      const data = await lawyerPartnersService.getPartners();
      setLawyerPartners(data || []);
    } catch (error) {
      setLawyerPartners([]);
    }
  };
  const analysisObj = useMemo(() => safeParseAnalysis(processo?.analise), [processo]);

  const memorandoPadrao = useMemo(() => {
    const raw =
      analysisObj?.memorandoPadrao?.text ??
      analysisObj?.memorandoTrabalhista?.text ??
      null;
    // NÃO decodificar aqui para evitar double decode (o texto final exibido
    // passa por decodeHtmlEntities uma única vez em `resumoIntegral`).
    return raw == null ? null : String(raw);
  }, [analysisObj]);

  const resumoIntegral = useMemo(() => {
    const raw = memorandoPadrao || processo?.resumo || null;
    return raw == null ? null : decodeHtmlEntities(String(raw));
  }, [processo, memorandoPadrao]);

  const resumoBadge = memorandoPadrao
    ? {
        label: "Parecer (ECP Capital)",
        className:
          "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200",
      }
    : {
        label: "Resumo do processo",
        className:
          "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200",
      };

  const scoringSummary = analysisObj?.scoring || null;
  const leadAutomation = analysisObj?.leadAutomation || null;
  const calculos = processo?.calculos || null;
  const calculoPrecatorio = processo?.calculoPrecatorio || null;

  const hasScoringContent = useMemo(
    () =>
      !!(
        processo?.recomendacao ||
        processo?.observacoes ||
        processo?.viabilidade ||
        scoringSummary
      ),
    [processo, scoringSummary],
  );

  const contactHints = useMemo(() => extractProcessContactHints(analysisObj), [analysisObj]);
  const hasContactHints =
    (contactHints?.emails?.length || 0) > 0 || (contactHints?.phones?.length || 0) > 0;

  const leadsCreatedCount = leadAutomation?.leadsCreatedCount || 0;

  // Leads criados automaticamente (via Kortex) podem ser descartados junto com o
  // processo, pois serão apenas ocultados (soft delete) da página de leads.
  const leadsCreatedAutomatically =
    leadAutomation?.lastAttemptMode === "AUTO" ||
    (leadAutomation?.autoAttempted &&
      leadAutomation?.lastAttemptMode !== "MANUAL");

  const canRejectProcess =
    !!processo &&
    processo.status === "ANALISE_CONCLUIDA" &&
    (leadsCreatedCount === 0 || leadsCreatedAutomatically);

  const canRestoreProcess = !!processo && processo.status === "REJEITADO";

  const kortexRefreshNotice = useMemo(() => {
    const requestedAt = analysisObj?.kortexRefresh?.requestedAt;
    if (!requestedAt) return null;
    if (
      processo?.status !== "AGUARDANDO_ANALISE" &&
      processo?.status !== "EM_ANALISE"
    ) {
      return null;
    }
    const formatted = new Date(requestedAt).toLocaleString("pt-BR");
    if (Number.isNaN(new Date(requestedAt).getTime())) return null;
    return `Consulta e análise reenviadas à Kortex em ${formatted}. O parecer é atualizado quando a análise terminar.`;
  }, [analysisObj, processo?.status]);

  const canAdminGenerateMemorando =
    isAdmin &&
    !!processo &&
    (processo.status === "ANALISE_CONCLUIDA" ||
      !!analysisObj?.results?.length ||
      !!analysisObj?.scoring);

  const showParecerSection =
    !!resumoIntegral || canAdminGenerateMemorando || hasScoringContent;

  const memorandoPreview = useMemo(() => {
    if (!resumoIntegral) return null;
    const firstLine = resumoIntegral.split("\n").find((line) => line.trim())?.trim();
    if (!firstLine) return null;
    return firstLine.length > 90 ? `${firstLine.slice(0, 90)}…` : firstLine;
  }, [resumoIntegral]);

  const handleGenerateMemorando = async () => {
    if (!id || memorandoGenerating) return;
    setMemorandoGenerating(true);
    setMemorandoError(null);
    try {
      const response = await apiService.post(`/processes/${id}/memorando/generate`, {});
      const generated = response?.memorandoPadrao;
      if (generated?.text) {
        setProcesso((prev) => {
          if (!prev) return prev;
          const currentAnalysis = safeParseAnalysis(prev.analise) || {};
          return {
            ...prev,
            score: response.score ?? prev.score,
            viabilidade: response.viabilidade ?? prev.viabilidade,
            recomendacao: response.recomendacao ?? prev.recomendacao,
            observacoes: response.observacoes ?? prev.observacoes,
            analise: {
              ...currentAnalysis,
              memorandoPadrao: generated,
              scoring: response.scoring ?? currentAnalysis.scoring,
            },
          };
        });
        setIsParecerExpanded(true);
      } else {
        await fetchProcesso();
      }
    } catch (err) {
      setMemorandoError(
        err?.message || "Não foi possível gerar o parecer. Tente novamente.",
      );
    } finally {
      setMemorandoGenerating(false);
    }
  };

  const handleRefreshKortex = async () => {
    if (!processo?.id || refreshingKortex) return;

    const rejectedNote =
      processo.status === "REJEITADO"
        ? " O processo sairá de Rejeitados e voltará a aguardar análise. Leads ocultados pelo descarte voltam a aparecer."
        : "";

    if (
      !window.confirm(
        `Refazer a consulta e a análise deste processo na Kortex? O parecer atual permanece até a nova análise terminar.${rejectedNote}`,
      )
    ) {
      return;
    }

    setRefreshingKortex(true);
    try {
      const response = await processesService.refreshKortexConsult(processo.id);
      alert(
        response?.userMessage ||
          "Consulta e análise reenviadas à Kortex.",
      );
      await fetchProcesso();
    } catch (error) {
      const apiError = error?.response?.data;
      const message =
        apiError?.error ||
        apiError?.message ||
        error?.message ||
        "Não foi possível reenviar a consulta e a análise para a Kortex.";
      alert(message);
    } finally {
      setRefreshingKortex(false);
    }
  };

  const handleConvertToLead = async () => {
    if (!processo || convertingToLead) return;
    setConvertingToLead(true);
    try {
      const response = await apiService.post(
        `/processes/${id}/convert-to-leads`,
        {},
      );

      const createdCount = response?.createdCount ?? 0;
      if (createdCount > 0) {
        alert(
          `Lead(s) criado(s) com sucesso: ${createdCount}.`,
        );
      } else {
        const errors = Array.isArray(response?.errors) ? response.errors : [];
        alert(
          `Nenhum lead foi criado. ${errors.length ? `\n\nDetalhes:\n- ${errors.join("\n- ")}` : ""}`,
        );
      }

      await fetchProcesso();
    } catch (error) {
      const apiError = error?.response?.data;
      const message =
        apiError?.error || apiError?.message || error?.message || "Erro ao converter processo em lead";
      alert(message);
    } finally {
      setConvertingToLead(false);
    }
  };

  const handleRejectProcess = async () => {
    if (!processo?.id || rejectingProcess || !canRejectProcess) return;

    if (
      !window.confirm(
        "Descartar este caso? Ele será movido para a aba Rejeitados e os leads gerados automaticamente a partir dele serão ocultados da página de leads.",
      )
    ) {
      return;
    }

    try {
      setRejectingProcess(true);
      await processesService.rejectProcess(processo.id);
      goBackToProcessList();
    } catch (error) {
      const apiError = error?.response?.data;
      const message =
        apiError?.error ||
        apiError?.message ||
        error?.message ||
        "Não foi possível rejeitar o processo.";
      alert(message);
    } finally {
      setRejectingProcess(false);
    }
  };

  const handleRestoreProcess = async () => {
    if (!processo?.id || restoringProcess || !canRestoreProcess) return;

    if (
      !window.confirm(
        "Restaurar este caso para análise concluída? Os leads ocultados pelo descarte voltarão a aparecer.",
      )
    ) {
      return;
    }

    try {
      setRestoringProcess(true);
      await processesService.restoreProcess(processo.id);
      await fetchProcesso();
    } catch (error) {
      const apiError = error?.response?.data;
      const message =
        apiError?.error ||
        apiError?.message ||
        error?.message ||
        "Não foi possível restaurar o processo.";
      alert(message);
    } finally {
      setRestoringProcess(false);
    }
  };

  const matchedPartners = useMemo(() => {
    const processLawyers = [];

    if (processo?.subjects?.length) {
      processo.subjects.forEach((subject) => {
        subject.advocates?.forEach((advocate) => {
          const nome = advocate?.people?.name;
          const oab = advocate?.people?.additional?.person?.registrationProfession;
          if (nome) processLawyers.push({ nome, oab });
        });
      });
    }

    if (cnjData?.participants?.length) {
      cnjData.participants.forEach((participant) => {
        if (participant?.typeName?.toUpperCase().includes("ADVOG")) {
          processLawyers.push({ nome: participant.participantName });
        }
      });
    }

    return findMatchingLawyerPartners({
      partners: lawyerPartners,
      lawyers: processLawyers,
      content: [processo?.numeroProcesso, processo?.resumo, processo?.conteudo]
        .filter(Boolean)
        .join(" "),
    });
  }, [processo, cnjData, lawyerPartners]);

  const sortedSubjects = useMemo(() => {
    if (!processo?.subjects?.length) return [];
    const poloPriority = (subject) => {
      const tipo = (subject.part?.subjectType || "").toLowerCase();
      if (tipo.includes("ativa") || tipo.includes("ativo")) return 0;
      if (tipo.includes("passiva") || tipo.includes("passivo")) return 1;
      return 2;
    };
    return [...processo.subjects].sort((a, b) => {
      const poloDiff = poloPriority(a) - poloPriority(b);
      if (poloDiff !== 0) return poloDiff;
      const hasPartyA = !!(a.part?.people?.name);
      const hasPartyB = !!(b.part?.people?.name);
      if (hasPartyA && !hasPartyB) return -1;
      if (!hasPartyA && hasPartyB) return 1;
      return 0;
    });
  }, [processo?.subjects]);

  const fetchProcesso = async () => {
    try {
      setLoading(true);
      setError(null);

      // Tentar primeiro na nova API de processos
      try {
        const processo = await apiService.get(`/processes/${id}`);
        setProcesso(processo);
        fetchProcessPublications(processo);
        return;
      } catch {
        // Nova API pode falhar ou não ter o recurso; fluxo segue para /justices.
      }

      // Fallback para API antiga
      const response = await apiService.get(`/justices/${id}`);

      if (response.success) {
        setProcesso(response.data);
        fetchProcessPublications(response.data);
      } else {
        throw new Error(
          response.message || "Erro ao buscar detalhes do processo",
        );
      }
    } catch (error) {
      // Error fetching process details
      setError(error.message || "Erro ao carregar detalhes do processo");
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessPublications = async (processoData) => {
    const numeroLimpo = getProcessNumberDigits(processoData);
    if (!numeroLimpo) {
      setPublicacoes([]);
      return;
    }

    try {
      setPublicacoesLoading(true);
      setPublicacoesError(null);

      const djeRes = await publicationsDjeService.getPublications({
        searchTerm: numeroLimpo,
        limit: PROCESS_PUBLICATIONS_FETCH_LIMIT,
        sortBy: "dataDisponibilizacao",
        sortOrder: "desc",
        includeArchived: true,
        newsPublications: false,
      });

      let items = (djeRes?.data || []).map((pub) =>
        mapDjePublicationToListItem(pub),
      );

      if (processoData?.publicationId) {
        try {
          const originDje = await publicationsDjeService.getPublicationById(
            processoData.publicationId,
          );
          if (originDje) {
            items.push(mapDjePublicationToListItem(originDje, { isOrigin: true }));
          }
        } catch {
          // Publicação de origem não encontrada
        }
      }

      setPublicacoes(sortProcessPublications(items));
    } catch (error) {
      setPublicacoesError(error.message || "Erro ao carregar publicações");
    } finally {
      setPublicacoesLoading(false);
    }
  };

  const MOVEMENTS_PER_PAGE = 50;

  const fetchMovements = async (processId, page = 1, append = false) => {
    try {
      if (append) {
        setMovementsLoadingMore(true);
      } else {
        setMovementsLoading(true);
        setMovementsError(null);
      }

      // order=desc: exibição mais recente primeiro (UI Kortex); API padrão é asc (movements-api.md)
      const movementQuery = {
        page,
        perPage: MOVEMENTS_PER_PAGE,
        field: "date_event",
        order: "desc",
      };
      const dataPromise = processesService.getProcessMovements(
        processId,
        movementQuery,
      );
      const secondDegreePromise = append
        ? Promise.resolve(null)
        : processesService
            .getProcessMovements(processId, {
              ...movementQuery,
              page: 1,
              instanceName: SECOND_DEGREE_INSTANCE_NAME,
            })
            .catch(() => null);

      const [data, secondDegree] = await Promise.all([
        dataPromise,
        secondDegreePromise,
      ]);
      const withSecondDegree = mergeMovementPages(
        data,
        secondDegree?.data || [],
      );

      setMovementsPage(page);
      setMovementsError(null);
      setMovementsResponse((prev) => {
        if (!append || !prev?.data?.length) {
          return withSecondDegree;
        }
        return mergeMovementPages(
          { ...data, data: prev.data },
          data?.data || [],
        );
      });
    } catch (error) {
      const status = error?.response?.status;
      const apiMessage =
        error?.response?.data?.error || error?.response?.data?.message;

      if (!append) {
        setMovementsResponse(null);
        setMovementsError(
          typeof apiMessage === "string" && apiMessage.trim()
            ? apiMessage
            : status === 404
              ? "Processo não encontrado."
              : "Não foi possível carregar as movimentações. Verifique sua conexão e tente novamente.",
        );
      }
    } finally {
      setMovementsLoading(false);
      setMovementsLoadingMore(false);
    }
  };

  const handleLoadMoreMovements = () => {
    if (!id || movementsLoadingMore) return;
    const nextPage = movementsPage + 1;
    fetchMovements(id, nextPage, true);
  };

  const fetchDocuments = async (processId) => {
    try {
      setDocumentsLoading(true);
      setDocumentsError(null);

      const data = await processesService.getProcessDocuments(processId);
      setDocumentsResponse(data);
    } catch (error) {
      const status = error?.response?.status;
      const apiMessage =
        error?.response?.data?.error || error?.response?.data?.message;

      setDocumentsResponse(null);
      setDocumentsError(
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage
          : status === 404
            ? "Processo não encontrado."
            : "Não foi possível carregar os documentos. Verifique sua conexão e tente novamente.",
      );
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchCNJData = async (processId) => {
    try {
      setCnjLoading(true);
      
      setCnjNotFound(false);
      const data = await apiService.get(`/processes/${processId}/cnj-data`);
      setCnjData(data);
    } catch (error) {
      const statusFromMessage =
        typeof error?.message === "string"
          ? Number(error.message.match(/status:\s*(\d{3})/)?.[1])
          : undefined;

      const status =
        error?.status || error?.response?.status || statusFromMessage;

      if (status === 404) {
        setCnjNotFound(true);
        setCnjData(null);
        return;
      }

      console.error("Erro ao carregar dados CNJ:", error);
      // Não exibe erro para o usuário pois é uma funcionalidade adicional
      setCnjData(null);
      setCnjNotFound(false);
    } finally {
      setCnjLoading(false);
    }
  };

  const mapPublicationForSidebar = (publicacao, detailedPublication = null) => {
    const publicationId = publicacao.id || publicacao._id;
    const detailedContent =
      detailedPublication?.texto ||
      publicacao.texto ||
      publicacao.resumo ||
      publicacao.conteudo ||
      publicacao.content ||
      "";
    const detailedParties =
      detailedPublication?.parties || publicacao.parties;
    const lawyerCandidates = extractActiveLawyerCandidates(detailedParties);

    return {
      ...publicacao,
      id: publicationId,
      numeroProcesso:
        detailedPublication?.numeroProcesso || publicacao.numeroProcesso,
      tribunal:
        detailedPublication?.tribunal ||
        detailedPublication?.siglaTribunal ||
        publicacao.tribunal ||
        publicacao.siglaTribunal,
      nomeOrgao: detailedPublication?.nomeOrgao || publicacao.nomeOrgao,
      score: detailedPublication?.score ?? publicacao.score ?? 0,
      classifications:
        detailedPublication?.classifications || publicacao.classifications || [],
      lida: detailedPublication?.lida ?? publicacao.lida,
      arquivada: detailedPublication?.arquivada ?? publicacao.arquivada,
      resumoIa: detailedPublication?.resumoIa,
      resumoIaGeneratedAt: detailedPublication?.resumoIaGeneratedAt,
      scoreBreakdown:
        detailedPublication?.scoreBreakdown || publicacao.scoreBreakdown,
      parties: detailedParties,
      displayParties: filterDisplayPoles(detailedParties),
      lawyerCandidates,
      matchedPartners: findMatchingLawyerPartners({
        partners: lawyerPartners,
        content: detailedContent,
        lawyers: lawyerCandidates,
      }),
      detalhes: {
        ...(publicacao.detalhes || {}),
        conteudo: detailedContent,
        ...(detailedPublication || {}),
      },
    };
  };

  const handlePublicationClick = async (item) => {
    const publicacao = item?.raw || item;

    try {
      setLoadingPublication(true);
      const publicationId = item.id || publicacao.id || publicacao._id;
      const detailedPublication =
        await publicationsDjeService.getPublicationById(publicationId);

      setSelectedPublication(
        mapPublicationForSidebar(publicacao, detailedPublication),
      );
      setIsPublicationSidebarOpen(true);
    } catch (error) {
      console.error("Erro ao buscar dados completos da publicação DJE:", error);
      setSelectedPublication(mapPublicationForSidebar(publicacao));
      setIsPublicationSidebarOpen(true);
    } finally {
      setLoadingPublication(false);
    }
  };

  const handleClosePublicationSidebar = () => {
    setIsPublicationSidebarOpen(false);
    setSelectedPublication(null);
  };

  const handlePartnerCreated = async (createdPartner) => {
    if (!createdPartner) return;

    await loadLawyerPartners();

    setSelectedPublication((current) => {
      if (!current) return current;
      const nextPartners = [createdPartner, ...(current.matchedPartners || [])];
      return {
        ...current,
        matchedPartners: nextPartners.filter(
          (partner, index, array) =>
            array.findIndex((item) => item.id === partner.id) === index,
        ),
      };
    });
  };

  const handlePublicationReclassified = (updatedPublication) => {
    setSelectedPublication((current) => {
      if (!current || current.id !== updatedPublication.id) return current;

      return {
        ...current,
        score: updatedPublication.score ?? current.score,
        scoreBreakdown:
          updatedPublication.scoreBreakdown ?? current.scoreBreakdown,
        arquivada: updatedPublication.arquivada ?? current.arquivada,
        classifications:
          updatedPublication.classifications ?? current.classifications,
        parties: updatedPublication.parties ?? current.parties,
        detalhes: {
          ...current.detalhes,
          arquivada:
            updatedPublication.arquivada ?? current.detalhes?.arquivada,
        },
      };
    });
  };

  const handlePublicationArchived = (publicationId, numeroProcesso) => {
    setPublicacoes((current) =>
      current.filter(
        (item) =>
          item.id !== publicationId &&
          (!numeroProcesso || item.numeroProcesso !== numeroProcesso),
      ),
    );
    setSelectedPublication(null);
    setIsPublicationSidebarOpen(false);
  };

  const visibleProcessPublications = useMemo(
    () =>
      publicacoes
        .filter((item) => !item.arquivada && !item.detalhes?.arquivada)
        .slice(0, PROCESS_PUBLICATIONS_DISPLAY_LIMIT),
    [publicacoes],
  );

  useEffect(() => {
    if (id) {
      fetchProcesso();
      fetchCNJData(id);
      fetchMovements(id);
      fetchDocuments(id);
      loadLawyerPartners();
    }
  }, [id]);

  const getStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
          Não especificado
        </span>
      );
    }

    // Status dos novos processos
    if (status === "AGUARDANDO_ANALISE") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
          Aguardando Análise
        </span>
      );
    }
    if (status === "AGUARDANDO_VALOR_OFICIO") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
          Aguardando valor do ofício
        </span>
      );
    }
    if (status === "EM_ANALISE") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
          Em Análise
        </span>
      );
    }
    if (status === "ANALISE_FALHOU") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          Falha na análise
        </span>
      );
    }
    if (status === "APROVADO") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700 border border-green-200">
          Aprovado
        </span>
      );
    }
    if (status === "REJEITADO") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          Rejeitado
        </span>
      );
    }
    if (status === "CONVERTIDO_LEAD") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
          Convertido em Lead
        </span>
      );
    }
    if (status === "ANALISE_CONCLUIDA") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700 border border-green-200">
          Análise Concluída
        </span>
      );
    }

    // Status dos processos antigos
    if (
      status.toLowerCase().includes("finalizado") ||
      status.toLowerCase().includes("transitada")
    ) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700 border border-green-200">
          Finalizado
        </span>
      );
    }
    if (status.toLowerCase().includes("andamento")) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
          Em Andamento
        </span>
      );
    }
    if (status.toLowerCase().includes("ativo")) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
          Ativo
        </span>
      );
    }
    if (status.toLowerCase().includes("recurso")) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
          Em Recurso
        </span>
      );
    }
    if (status.toLowerCase().includes("aguardando")) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
          Aguardando
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
        {status}
      </span>
    );
  };

  const getViabilidadeBadge = (viabilidade) => {
    if (!viabilidade) return null;

    if (viabilidade === "ALTA") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700 border border-green-200">
          Viabilidade Alta
        </span>
      );
    }

    if (viabilidade === "MEDIA") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
          Viabilidade Média
        </span>
      );
    }

    if (viabilidade === "BAIXA") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
          Viabilidade Baixa
        </span>
      );
    }

    if (viabilidade === "INCONCLUSIVO") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
          Inconclusivo
        </span>
      );
    }

    if (viabilidade === "INVIAVEL") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          Inviável
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
        {viabilidade}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const getDecisionBadge = (scoring, automation) => {
    const leadsCreatedCount = automation?.leadsCreatedCount || 0;

    if (automation?.finalStatus === "CONVERTIDO_LEAD" || leadsCreatedCount > 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
          Lead vinculado ao processo
        </span>
      );
    }

    if (scoring?.automationStatus === "MANUAL_READY" || automation?.eligible) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
          Elegível para criação manual de lead
        </span>
      );
    }

    if (scoring?.automationStatus === "MANUAL_REVIEW" || scoring?.decisionStatus === "REVISAR_MANUAL") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
          Revisão manual
        </span>
      );
    }

    if (scoring?.automationStatus === "REJECT" || scoring?.decisionStatus === "INVIAVEL") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          Não elegível no momento
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
        Monitorar / revisar
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
          <div className="flex items-center justify-center mb-4">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-gray-700 font-medium">
            Carregando detalhes do processo...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-red-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-red-700 font-medium">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  const laborLeadMissing = Array.isArray(
    scoringSummary?.laborLeadRequirements?.missing,
  )
    ? scoringSummary.laborLeadRequirements.missing
    : [];
  const laborLeadBlocked =
    scoringSummary?.justiceContext === "trabalho" && laborLeadMissing.length > 0;

  if (!processo) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Processo não encontrado
          </h3>
          <p className="text-gray-600">
            O processo solicitado não foi encontrado ou não existe.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Detalhes do Processo
            </h1>
            <ProcessNumber
              processNumber={
                processo.numeroProcesso ||
                processo.judgment?.number ||
                processo._id
              }
              className="text-gray-700 font-medium"
              copyable={true}
            />
            {kortexRefreshNotice && (
              <p className="mt-2 text-xs text-slate-600">{kortexRefreshNotice}</p>
            )}
            {laborLeadBlocked && (
              <p className="mt-2 text-xs text-amber-700">
                Para enviar o lead trabalhista faltam: {laborLeadMissing.join(", ")}.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <button
              type="button"
              onClick={handleRefreshKortex}
              disabled={
                refreshingKortex ||
                convertingToLead ||
                rejectingProcess ||
                restoringProcess
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border bg-white text-slate-700 border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title="Refaz a consulta e a análise na Kortex"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${refreshingKortex ? "animate-spin" : ""}`}
                aria-hidden
              />
              {refreshingKortex ? "Enviando..." : "Atualizar processo"}
            </button>
            {canRestoreProcess && (
              <button
                type="button"
                onClick={handleRestoreProcess}
                disabled={
                  restoringProcess ||
                  convertingToLead ||
                  rejectingProcess ||
                  refreshingKortex
                }
                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold border bg-white text-slate-700 border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Restaurar caso rejeitado"
              >
                {restoringProcess ? "Salvando..." : "Restaurar"}
              </button>
            )}
            {canRejectProcess && (
              <button
                type="button"
                onClick={handleRejectProcess}
                disabled={
                  rejectingProcess ||
                  convertingToLead ||
                  restoringProcess ||
                  refreshingKortex
                }
                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold border bg-red-50 text-red-700 border-red-200 hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Rejeitar caso sem enviar para leads"
              >
                {rejectingProcess ? "Salvando..." : "Rejeitar"}
              </button>
            )}
            <button
              type="button"
              onClick={handleConvertToLead}
              disabled={
                convertingToLead ||
                rejectingProcess ||
                restoringProcess ||
                refreshingKortex ||
                laborLeadBlocked
              }
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                convertingToLead || laborLeadBlocked
                  ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
                  : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
              }`}
              title={
                laborLeadBlocked
                  ? `Para a Justiça do Trabalho, o lead exige procedente, acórdão, sentença e valor. Pendente: ${laborLeadMissing.join(", ")}.`
                  : "Cria lead(s) a partir do processo manualmente"
              }
            >
              {convertingToLead
                ? "Criando lead(s)..."
                : leadsCreatedCount > 0
                  ? "Criar lead novamente"
                  : "Transformar processo em lead"}
            </button>
            <button
              type="button"
              onClick={goBackToProcessList}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation flex-shrink-0"
              aria-label="Fechar detalhes"
              title="Voltar para listagem de processos"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Parecer — documento + sinais comerciais/jurídicos */}
        {showParecerSection && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setIsParecerExpanded(!isParecerExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left gap-3"
              aria-expanded={isParecerExpanded}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">Parecer</h3>
                  {!isParecerExpanded && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {memorandoPreview
                        ? `${memorandoPreview} — clique para expandir`
                        : canAdminGenerateMemorando
                          ? "Ainda não gerado — clique para expandir ou use Gerar parecer"
                          : hasScoringContent
                            ? `Score ${processo.score || 0}/100 — clique para expandir`
                            : "Clique para expandir"}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isParecerExpanded && (
                  <>
                    <div className="hidden sm:flex items-center gap-2 flex-wrap justify-end">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                        Score {processo.score || 0}/100
                      </span>
                      {getViabilidadeBadge(processo.viabilidade || scoringSummary?.viability)}
                      {getDecisionBadge(scoringSummary, leadAutomation)}
                    </div>
                    {canAdminGenerateMemorando && !resumoIntegral && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleGenerateMemorando();
                        }}
                        disabled={memorandoGenerating}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                        title="Gerar parecer via LLM"
                      >
                        <RefreshCw
                          className={`w-3.5 h-3.5 ${memorandoGenerating ? "animate-spin" : ""}`}
                          aria-hidden
                        />
                        {memorandoGenerating ? "Gerando..." : "Gerar parecer"}
                      </button>
                    )}
                  </>
                )}
                {isParecerExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" aria-hidden />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" aria-hidden />
                )}
              </div>
            </button>

            {!isParecerExpanded && memorandoError && (
              <div className="px-6 pb-4 border-t border-gray-100">
                <div className="pt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {memorandoError}
                </div>
              </div>
            )}

            {isParecerExpanded && (
              <div className="px-6 pb-6 border-t border-gray-100">
                <div className="pt-4 flex items-center gap-2 flex-wrap mb-4">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                    Score {processo.score || 0}/100
                  </span>
                  {getViabilidadeBadge(processo.viabilidade || scoringSummary?.viability)}
                  {getDecisionBadge(scoringSummary, leadAutomation)}
                  <span className={resumoBadge.className}>{resumoBadge.label}</span>

                  {canAdminGenerateMemorando && (
                    <button
                      type="button"
                      onClick={handleGenerateMemorando}
                      disabled={memorandoGenerating}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                      title={
                        memorandoPadrao
                          ? "Regenerar parecer via LLM"
                          : "Gerar parecer via LLM"
                      }
                    >
                      <RefreshCw
                        className={`w-3.5 h-3.5 ${memorandoGenerating ? "animate-spin" : ""}`}
                        aria-hidden
                      />
                      {memorandoGenerating
                        ? "Gerando..."
                        : memorandoPadrao
                          ? "Gerar novamente"
                          : "Gerar parecer"}
                    </button>
                  )}

                  {resumoIntegral && (
                    <CopyButton
                      text={resumoIntegral}
                      label={memorandoPadrao ? "Copiar parecer" : "Copiar resumo"}
                    />
                  )}
                </div>

                {memorandoError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {memorandoError}
                  </div>
                )}

                {leadAutomation?.autoAttempted && leadsCreatedCount === 0 && (
                  <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-800">
                      A automação tentou criar lead(s) automaticamente, mas não concluiu.
                      Você pode usar o botão &quot;Transformar processo em lead&quot;.
                    </p>
                    {Array.isArray(leadAutomation?.errors) &&
                      leadAutomation.errors.length > 0 && (
                        <ul className="mt-2 text-sm text-yellow-700 list-disc pl-5 space-y-1">
                          {leadAutomation.errors.slice(0, 5).map((err, idx) => (
                            <li key={`auto-error-${idx}`}>
                              {decodeHtmlEntities(String(err))}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                )}

                {resumoIntegral ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4">
                    {memorandoPadrao ? (
                      <MemorandoContent text={resumoIntegral} />
                    ) : (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {resumoIntegral}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-600 mb-4">
                    Nenhum parecer ou resumo disponível. Use &quot;Gerar parecer&quot; para
                    produzir o documento a partir da análise do processo.
                  </div>
                )}

                {(scoringSummary?.parecer ||
                  processo.recomendacao ||
                  scoringSummary?.recommendation ||
                  processo.observacoes ||
                  scoringSummary?.observations) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <details className="bg-gray-50 border border-gray-200 rounded-lg group">
                      <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Parecer (Questionário)
                        </span>
                        <ChevronDown
                          className="w-4 h-4 text-gray-500 flex-shrink-0 group-open:rotate-180 transition-transform"
                          aria-hidden
                        />
                      </summary>
                      <div className="px-4 pb-4 border-t border-gray-100">
                        <div className="pt-3 flex justify-end mb-2">
                          {(scoringSummary?.parecer ||
                            processo.recomendacao ||
                            scoringSummary?.recommendation) && (
                            <CopyButton
                              text={decodeHtmlEntities(
                                String(
                                  scoringSummary?.parecer ||
                                    processo.recomendacao ||
                                    scoringSummary?.recommendation ||
                                    "",
                                ),
                              )}
                              label="Copiar"
                            />
                          )}
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                          {decodeHtmlEntities(
                            String(
                              scoringSummary?.parecer ||
                                processo.recomendacao ||
                                scoringSummary?.recommendation ||
                                "N/A",
                            ),
                          )}
                        </p>
                      </div>
                    </details>

                    <details className="bg-gray-50 border border-gray-200 rounded-lg group">
                      <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Observações
                        </span>
                        <ChevronDown
                          className="w-4 h-4 text-gray-500 flex-shrink-0 group-open:rotate-180 transition-transform"
                          aria-hidden
                        />
                      </summary>
                      <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                          {decodeHtmlEntities(
                            String(
                              processo.observacoes || scoringSummary?.observations || "N/A",
                            ),
                          )}
                        </p>
                      </div>
                    </details>
                  </div>
                )}

                {scoringSummary?.positiveSignals?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Sinais Positivos
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {scoringSummary.positiveSignals.map((signal, index) => (
                        <span
                          key={`signal-${index}`}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200"
                        >
                          {decodeHtmlEntities(String(signal))}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {scoringSummary?.blockers?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Impedimentos
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {scoringSummary.blockers.map((blocker, index) => (
                        <span
                          key={`blocker-${index}`}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-200"
                        >
                          {decodeHtmlEntities(String(blocker))}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {scoringSummary?.warnings?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Alertas
                    </p>
                    <div className="space-y-2">
                      {scoringSummary.warnings.map((warning, index) => (
                        <div
                          key={`warning-${index}`}
                          className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800"
                        >
                          {decodeHtmlEntities(String(warning))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {calculoPrecatorio && (
                  <CalculosCreditoSection
                    calculoPrecatorio={calculoPrecatorio}
                    variant="embedded"
                  />
                )}

                {calculos && (
                  <CalculosValorPresenteSection calculos={calculos} variant="embedded" />
                )}
              </div>
            )}
          </div>
        )}

        {/* Análise (Kortex) */}
        <ProcessAnalysisPanel
          analysis={processo?.analise}
          isExpanded={isAnalysisExpanded}
          onToggle={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
        />

        <ProcessCNJDataPanel
          cnjData={cnjData}
          loading={cnjLoading}
          cnjNotFound={cnjNotFound}
          isExpanded={isCNJDataExpanded}
          onToggle={() => setIsCNJDataExpanded(!isCNJDataExpanded)}
          cadastroMeta={
            <>
              <div className="flex items-center gap-2 flex-wrap">
                {getStatusBadge(processo.status || processo.situation)}
                {(processo.isDigital || processo.score >= 66) && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                    {processo.score ? `⭐ Score: ${processo.score}` : "Digital"}
                  </span>
                )}
              </div>
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Classificação
                </span>
                <p className="font-medium text-gray-900 mt-1">
                  {processo.classificacoes?.join(", ") ||
                    (processo.judgment?.className
                      ? processo.judgment.className.split("\\").pop()?.trim()
                      : "N/A")}
                </p>
              </div>
            </>
          }
        />

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 gap-3 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              <Mail className="w-4 h-4 text-gray-600 mr-2" />
              Dados de contato (autor / advogado)
            </h3>
          </div>

          {!analysisObj ? (
            <p className="text-sm text-gray-600">
              As informações de contato dependem da análise concluída no Kortex. Quando a análise estiver disponível,
              e-mails e telefones informados nas respostas do questionário poderão ser exibidos aqui.
            </p>
          ) : !hasContactHints ? (
            <p className="text-sm text-gray-600">
              Nenhum e-mail ou telefone foi identificado automaticamente nas respostas do Q&A da análise. Confira o painel
              &quot;Análise (Kortex)&quot; acima ou inclua perguntas no template que solicitem contato do advogado ou da
              parte.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" />
                    E-mails
                  </p>
                  <ul className="space-y-2">
                    {contactHints.emails.map((email) => (
                      <li key={email}>
                        <a
                          href={`mailto:${email}`}
                          className="text-sm font-medium text-blue-700 hover:text-blue-800 break-all"
                        >
                          {email}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" />
                    Telefones
                  </p>
                  <ul className="space-y-2">
                    {contactHints.phones.map((phone) => {
                      const digits = phone.replace(/\D/g, "");
                      const telHref =
                        digits.length >= 10 && digits.length <= 11
                          ? `+55${digits}`
                          : digits || phone;
                      return (
                        <li key={phone}>
                          <a
                            href={`tel:${telHref}`}
                            className="text-sm font-medium text-blue-700 hover:text-blue-800"
                          >
                            {phone}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {Array.isArray(contactHints.sources) && contactHints.sources.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-xs font-semibold text-gray-600 hover:text-gray-800">
                    Fontes no questionário (Kortex)
                  </summary>
                  <ul className="mt-3 space-y-2 text-sm text-gray-700">
                    {contactHints.sources.map((src, idx) => (
                      <li
                        key={`contact-src-${idx}`}
                        className="border border-gray-100 rounded-lg p-3 bg-gray-50"
                      >
                        <p className="text-xs font-semibold text-gray-500 mb-1">
                          {decodeHtmlEntities(String(src.question || ""))}
                        </p>
                        {(src.emails?.length > 0 || src.phones?.length > 0) && (
                          <p className="text-xs text-gray-600">
                            {src.emails?.join(", ")}
                            {src.emails?.length && src.phones?.length ? " / " : ""}
                            {src.phones?.join(", ")}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <ProcessMovementsPanel
          movementsResponse={movementsResponse}
          loading={movementsLoading}
          error={movementsError}
          onLoadMore={handleLoadMoreMovements}
          loadingMore={movementsLoadingMore}
          isExpanded={isMovementsExpanded}
          onToggle={() => setIsMovementsExpanded(!isMovementsExpanded)}
        />

        <ProcessDocumentsPanel
          documentsResponse={documentsResponse}
          loading={documentsLoading}
          error={documentsError}
          isExpanded={isDocumentsExpanded}
          onToggle={() => setIsDocumentsExpanded(!isDocumentsExpanded)}
        />

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              <Scale className="w-4 h-4 text-gray-600 mr-2" />
              Advogados parceiros relacionados
            </h3>
          </div>

          {matchedPartners.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {matchedPartners.map((partner) => (
                <PartnerLawyerBadge key={partner.id} partner={partner} compact={true} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              Nenhum advogado parceiro relacionado a este processo foi encontrado até o momento.
            </div>
          )}
        </div>


        {/* Publicações do Processo */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <button
            type="button"
            onClick={() => setIsPublicationsExpanded(!isPublicationsExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl text-left"
            aria-expanded={isPublicationsExpanded}
          >
            <div className="flex items-center gap-2 min-w-0">
              <BookOpen className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold text-gray-900">
                    Publicações do Processo
                  </h3>
                  <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
                    {publicacoes.length}
                  </span>
                </div>
                {!isPublicationsExpanded && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {publicacoes.length === 0
                      ? "Nenhuma publicação DJE vinculada — clique para expandir"
                      : `${publicacoes.length} publicação${
                          publicacoes.length === 1 ? "" : "ões"
                        } DJE — clique para expandir`}
                  </p>
                )}
              </div>
            </div>
            {isPublicationsExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" aria-hidden />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" aria-hidden />
            )}
          </button>

          {isPublicationsExpanded && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="mt-4">
                {publicacoesLoading && (
                  <div className="flex items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mr-2" />
                    <span className="text-gray-700">
                      Carregando publicações...
                    </span>
                  </div>
                )}

                {publicacoesError && (
                  <div className="bg-white border border-red-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-red-700 font-medium">
                        {publicacoesError}
                      </span>
                    </div>
                  </div>
                )}

                {!publicacoesLoading &&
                  !publicacoesError &&
                  publicacoes.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-base font-semibold text-gray-900 mb-2">
                        Nenhuma publicação encontrada
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Não foram encontradas publicações para este processo.
                      </p>
                    </div>
                  )}

                {!publicacoesLoading &&
                  !publicacoesError &&
                  publicacoes.length > 0 && (
                    <div className="space-y-3">
                      {visibleProcessPublications.map((item, index) => {
                        const raw = item.raw || {};
                        const previewText =
                          item.summary ||
                          item.content ||
                          raw.texto ||
                          raw.resumo ||
                          "Conteúdo não disponível";
                        const previewDisplay =
                          String(previewText).length > 200
                            ? `${String(previewText).substring(0, 200)}...`
                            : String(previewText);
                        const nomeOrgao = raw.nomeOrgao;

                        return (
                          <div
                            key={`${item.id}-${index}`}
                            className={`border border-gray-200 rounded-lg p-4 bg-white hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group ${
                              loadingPublication
                                ? "pointer-events-none opacity-50"
                                : ""
                            } ${item.isOrigin ? "ring-1 ring-emerald-200" : ""}`}
                            onClick={() => handlePublicationClick(item)}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  {item.isOrigin && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      Origem do processo
                                    </span>
                                  )}
                                  {item.scoreClassification && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                                        item.scoreClassification === "EXCEPCIONAL"
                                          ? "bg-purple-100 text-purple-700 border-purple-200"
                                          : item.scoreClassification === "PROVÁVEL"
                                            ? "bg-green-100 text-green-700 border-green-200"
                                            : item.scoreClassification === "POSSÍVEL"
                                              ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                                              : item.scoreClassification === "REMOTO"
                                                ? "bg-orange-100 text-orange-700 border-orange-200"
                                                : item.scoreClassification === "INAPTO"
                                                  ? "bg-red-100 text-red-700 border-red-200"
                                                  : "bg-gray-100 text-gray-700 border-gray-200"
                                      }`}
                                    >
                                      {item.scoreClassification}
                                    </span>
                                  )}
                                  {typeof item.score === "number" && (
                                    <div className="flex items-center gap-1">
                                      <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                      <span className="text-xs text-gray-600">
                                        {item.score}
                                      </span>
                                    </div>
                                  )}
                                  <span className="text-xs text-gray-500">
                                    {item.datePublication
                                      ? new Date(item.datePublication).toLocaleDateString(
                                          "pt-BR",
                                        )
                                      : "Data não informada"}
                                  </span>
                                </div>

                                <p className="font-medium text-gray-900 text-sm mb-2">
                                  {item.tribunal || "Tribunal não informado"}
                                  {nomeOrgao ? ` — ${nomeOrgao}` : ""}
                                  {!nomeOrgao && item.source
                                    ? ` — ${item.source}`
                                    : ""}
                                </p>

                                <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                                  {decodeHtmlEntities(previewDisplay)}
                                </p>
                              </div>

                              <Eye className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0 group-hover:text-blue-500 transition-colors" />
                            </div>

                            {item.classifications?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {item.classifications.slice(0, 3).map((classification, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                  >
                                    {typeof classification === "string"
                                      ? classification
                                      : classification.name}
                                  </span>
                                ))}
                                {item.classifications.length > 3 && (
                                  <span className="text-xs text-gray-500">
                                    +{item.classifications.length - 3} mais
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {publicacoes.length > PROCESS_PUBLICATIONS_DISPLAY_LIMIT && (
                        <div className="text-center pt-4">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/publicacoes-dje?searchTerm=${getProcessNumberDigits(processo)}`,
                              )
                            }
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                          >
                            Ver todas as {publicacoes.length} publicações
                            <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {/* Loading indicator para busca de publicação completa */}
                {loadingPublication && (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-5 h-5 text-blue-600 animate-spin mr-2" />
                    <span className="text-sm text-gray-600">
                      Carregando detalhes da publicação...
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>


        {/* Partes do Processo */}
        {sortedSubjects.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center pb-3 border-b border-gray-100">
              <User className="w-4 h-4 text-gray-600 mr-2" />
              Partes do Processo
              <span className="ml-2 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                {sortedSubjects.length}
              </span>
            </h3>

            <div className="space-y-3">
              {sortedSubjects.map((subject, index) => {
                const isParteAtiva = subject.part?.subjectType === "Parte Ativa";
                const partyName = subject.part?.people?.name;
                const hasAdvocates =
                  subject.advocates && subject.advocates.length > 0;

                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-colors"
                  >
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border mb-3 ${
                        isParteAtiva
                          ? "bg-green-100 text-green-700 border-green-200"
                          : "bg-red-100 text-red-700 border-red-200"
                      }`}
                    >
                      {subject.part?.subjectType || "N/A"}
                    </span>

                    <div className="text-sm space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Parte
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {partyName || "Nome não informado"}
                        </p>
                        <p className="text-gray-600 text-xs mt-1">
                          Tipo: {subject.part?.people?.personalType || "N/A"}
                        </p>
                      </div>

                      {hasAdvocates && (
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Advogado(s)
                          </p>
                          <ul className="space-y-2">
                            {subject.advocates.map((advocate, advIndex) => (
                              <li
                                key={advIndex}
                                className="text-sm text-gray-800 pl-3 border-l-2 border-gray-300"
                              >
                                {advocate.people?.name || "Nome não informado"}
                                {advocate.people?.additional?.person
                                  ?.registrationProfession &&
                                  ` — ${advocate.people.additional.person.registrationProfession}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedPublication && (
        <PublicationDjeDetailsSidebar
          publication={selectedPublication}
          isOpen={isPublicationSidebarOpen}
          onClose={handleClosePublicationSidebar}
          onPartnerCreated={handlePartnerCreated}
          onPublicationReclassified={handlePublicationReclassified}
          onPublicationArchived={handlePublicationArchived}
        />
      )}
    </div>
  );
}
